import { type DragEvent as ReactDragEvent } from "react";
import { type Organization } from "../api/client";
import { type DecryptedItem } from "../vault/items";
import { type MenuAction } from "./Menu";
import { type FolderNode } from "./folder-tree";
import { NodeIcon, Icons } from "./icons";
import { useT } from "../i18n";

type NavigatorTreeProps = {
  personalOrg: Organization | null;
  clientOrgs: Organization[];
  selectedOrg: Organization | null;
  mode: "org" | "shared" | "trash";
  readOnly: boolean;
  items: DecryptedItem[];
  folders: FolderNode[];
  selectedFolderId: string | null;
  setSelectedFolderId: (id: string | null) => void;
  collapsed: Set<string>;
  toggleCollapse: (id: string) => void;
  childrenOf: Map<string | null, FolderNode[]>;
  nodeCount: (id: string) => number;
  nodeHidden: (f: FolderNode) => boolean;
  dropTarget: string | null;
  onNodeDragOver: (e: ReactDragEvent, target: string) => void;
  onNodeDrop: (e: ReactDragEvent, target: string) => Promise<void>;
  onNodeDragStartTree: (nodeId: string, e: ReactDragEvent) => void;
  openOrg: (org: Organization, keepFolder?: boolean) => Promise<void>;
  openShared: () => Promise<void>;
  createOrg: () => Promise<void>;
  deleteFolderUI: (id: string) => Promise<void>;
  setEditNode: (n: { id: string; kind: "folder" | "device"; name: string; category?: string; address?: string }) => void;
  setMenu: (m: { x: number; y: number; actions: MenuAction[] }) => void;
  nodeCreateActions: (parentId: string | null) => MenuAction[];
};

/** Left navigator: personal vault, client vaults with their folder/device trees, shared-with-me. */
export function NavigatorTree({
  personalOrg,
  clientOrgs,
  selectedOrg,
  mode,
  readOnly,
  items,
  folders,
  selectedFolderId,
  setSelectedFolderId,
  collapsed,
  toggleCollapse,
  childrenOf,
  nodeCount,
  nodeHidden,
  dropTarget,
  onNodeDragOver,
  onNodeDrop,
  onNodeDragStartTree,
  openOrg,
  openShared,
  createOrg,
  deleteFolderUI,
  setEditNode,
  setMenu,
  nodeCreateActions,
}: NavigatorTreeProps) {
  const t = useT();

  // One navigator row for an org plus, when open, its folder tree.
  const orgRow = (o: Organization) => (
    <div key={o.id}>
      <div
        className={`group flex w-full items-center gap-2 px-3 py-1.5 ${
          selectedOrg?.id === o.id && mode === "org" && dropTarget === "__root__" ? "ring-1 ring-inset ring-accent" : ""
        } ${
          selectedOrg?.id === o.id && mode === "org" && selectedFolderId === null ? "bg-accent/20" : "hover:bg-panel2"
        }`}
        onDragOver={selectedOrg?.id === o.id && mode === "org" ? (e) => onNodeDragOver(e, "__root__") : undefined}
        onDrop={selectedOrg?.id === o.id && mode === "org" ? (e) => onNodeDrop(e, "__root__") : undefined}
        onContextMenu={(e) => {
          if (o.is_personal) return;
          e.preventDefault();
          void openOrg(o);
          setMenu({ x: e.clientX, y: e.clientY, actions: nodeCreateActions(null) });
        }}
      >
        <button onClick={() => openOrg(o)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <span className="text-muted">{o.is_personal ? <Icons.vaultPersonal className="h-4 w-4" /> : <Icons.vaultOrg className="h-4 w-4" />}</span>
          <span className="truncate">{o.is_personal ? t("Personal vault") : o.name}</span>
        </button>
        {selectedOrg?.id === o.id && mode === "org" && (
          <>
            <span className="shrink-0 font-mono text-[11px] text-muted group-hover:hidden">{items.length}</span>
            {!readOnly && (
            <button
              title={t("Add here")}
              onClick={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setMenu({ x: r.left, y: r.bottom + 2, actions: nodeCreateActions(null) });
              }}
              className="hidden shrink-0 px-1 text-muted hover:text-fg group-hover:block"
            >
              <Icons.plus className="h-3.5 w-3.5" />
            </button>
            )}
          </>
        )}
      </div>
      {selectedOrg?.id === o.id &&
        mode === "org" &&
        folders
          .filter((f) => !nodeHidden(f))
          .map((f) => {
            const kids = (childrenOf.get(f.id) ?? []).length > 0;
            const count = nodeCount(f.id);
            return (
              <div
                key={f.id}
                draggable
                onDragStart={(e) => onNodeDragStartTree(f.id, e)}
                style={{ paddingLeft: 4 + f.depth * 14 }}
                className={`group flex w-full items-center gap-1 py-1 pr-2 ${dropTarget === f.id ? "ring-1 ring-inset ring-accent" : ""} ${selectedFolderId === f.id ? "bg-accent/20" : "hover:bg-panel2"}`}
                onDragOver={(e) => onNodeDragOver(e, f.id)}
                onDrop={(e) => onNodeDrop(e, f.id)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setSelectedFolderId(f.id);
                  setMenu({
                    x: e.clientX,
                    y: e.clientY,
                    actions: [
                      ...nodeCreateActions(f.id),
                      { label: t("Rename"), onClick: () => setEditNode({ id: f.id, kind: f.kind, name: f.name, category: f.meta?.category, address: f.meta?.address }) },
                      { label: t("Delete"), danger: true, onClick: () => void deleteFolderUI(f.id) },
                    ],
                  });
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (kids) toggleCollapse(f.id);
                  }}
                  className={`grid w-3.5 shrink-0 place-items-center text-muted ${kids ? "hover:text-fg" : "invisible"}`}
                >
                  {collapsed.has(f.id) ? <Icons.chevronRight className="h-3.5 w-3.5" /> : <Icons.chevronDown className="h-3.5 w-3.5" />}
                </button>
                <button onClick={() => setSelectedFolderId(f.id)} className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
                  <span className="text-muted"><NodeIcon node={f} className="h-3.5 w-3.5" /></span>
                  <span className="truncate" title={f.kind === "device" && f.meta?.address ? f.meta.address : undefined}>{f.name}</span>
                </button>
                <span className="shrink-0 font-mono text-[11px] text-muted group-hover:hidden">{count || ""}</span>
                <span className="hidden shrink-0 items-center gap-1.5 text-muted group-hover:flex">
                  <button
                    title={t("Add here")}
                    onClick={(e) => {
                      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      setMenu({ x: r.left, y: r.bottom + 2, actions: nodeCreateActions(f.id) });
                    }}
                    className="hover:text-fg"
                  >
                    <Icons.plus className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setEditNode({ id: f.id, kind: f.kind, name: f.name, category: f.meta?.category, address: f.meta?.address })}
                    title={t("Edit")}
                    className="hover:text-fg"
                  >
                    <Icons.edit className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => deleteFolderUI(f.id)} title={t("Delete")} className="hover:text-danger"><Icons.trash className="h-3.5 w-3.5" /></button>
                </span>
              </div>
            );
          })}
    </div>
  );

  return (
    <nav className="flex w-64 flex-col border-r border-border bg-panel">
      <div className="min-h-0 flex-1 overflow-auto">
        {/* Personal vault — pinned at the top, visible only to its owner. */}
        {personalOrg && (
          <>
            <div className="eyebrow px-3 pt-2 pb-1">{t("PERSONAL")}</div>
            {orgRow(personalOrg)}
            <div className="my-1 border-t border-border" />
          </>
        )}
        <div className="flex items-center justify-between px-3 py-2">
          <span className="eyebrow">{t("VAULTS")}</span>
          <button onClick={createOrg} title="New vault" className="rounded px-1.5 text-muted hover:bg-panel2 hover:text-fg">
            <Icons.plus className="h-3.5 w-3.5" />
          </button>
        </div>
        {clientOrgs.length === 0 && <div className="px-3 py-2 text-xs text-muted">{t("No vaults yet — create one with +.")}</div>}
        {clientOrgs.map((o) => orgRow(o))}
        <div className="my-1 border-t border-border" />
        <button
          onClick={openShared}
          className={`flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-panel2 ${
            mode === "shared" ? "bg-accent/20" : ""
          }`}
        >
          <span className="text-muted"><Icons.shared className="h-4 w-4" /></span>
          <span className="truncate">{t("Shared with me")}</span>
        </button>
      </div>
    </nav>
  );
}
